import type { NoticeBlockProps } from "@/components/NoticeBlock.tsx";
import { digioLoader, type DigioSDK } from "@/modules/billing/account_validation/digio/digioLoader.ts";
import { StartDigioValidationButton } from "@/modules/billing/account_validation/digio/StartDigioValidationButton.tsx";
import { DigioLoadError } from "@/modules/billing/account_validation/digio/utils.ts";
import {
    GeneralBaValidationBlock,
    type GeneralBaValidationBlockProps,
} from "@/modules/billing/account_validation/GeneralBaValidationBlock.tsx";
import { RefreshStatusButton } from "@/modules/billing/account_validation/RefreshStatusButton.tsx";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { ErrorMessage } from "@/modules/error/ErrorFallback.tsx";
import { getTransText, type Translatable } from "@/modules/l10n/getTransText.ts";
import { ContactSupportButton } from "@/modules/support/ContactSupportButton.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MainConfig } from "@warrenio/api-spec/spec.oats.gen";
import { useEffect, useState, type ReactNode } from "react";

export type DigioProviderConfig = MainConfig["billingAccountValidation"] & { process: "EXTERNAL"; provider: "digio" };

type DigioBaValidationBlockProps = GeneralBaValidationBlockProps & {
    billing_account: EBillingAccount;
    config: DigioProviderConfig;
};

export function DigioBaValidationBlock(props: DigioBaValidationBlockProps) {
    const { config, billing_account, ...generalProps } = props;

    const { i18n, t } = useLingui();
    const [loadError, setLoadError] = useState<DigioLoadError | undefined>(undefined);
    const [digio, setDigio] = useState<DigioSDK | undefined>(undefined);
    const [showRefresh, setShowRefresh] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async () => {
            try {
                const loadResult = await digioLoader(config);
                const digioSDK = new loadResult.digio({ environment: config.test_mode ? "sandbox" : "production" });
                setDigio(digioSDK);
            } catch (error) {
                const digioError = new DigioLoadError({ cause: error });
                setLoadError(digioError);
                throw digioError;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let title: Translatable | undefined;
    let description: Translatable;
    let button: ReactNode[] | undefined;
    let color: NoticeBlockProps["color"];
    let mentionEmailLink = true;

    if (
        billing_account.isKycProcessCompleted ||
        (billing_account.isKycApprovedByProvider && !config.provider_conf.auto_accept)
    ) {
        // in case of:
        // - KYC form completed by user but decision not yet made ("approved" or "rejected")
        // - KYC is approved but automatic BA validation acceptance is disabled - needs admin intervention
        // (if automatic acceptance is enabled, then BA status is set from PENDING_VALIDATION to VALID and this validation block will not be displayed to user anymore)
        description = getTransText(
            <Trans>You have completed billing account validation process. Please wait for the final decision.</Trans>,
            i18n,
        );

        button = [<RefreshStatusButton key="refresh" />];
    } else if (billing_account.isKycRejectedByProvider) {
        description = getTransText(<Trans>Please contact the administrators for further details.</Trans>, i18n);
        title = t`Billing account validation was rejected`;
        button = [<ContactSupportButton key="support" color="error" />];
        color = "error";
    } else {
        const isInProgress = billing_account.isKycProcessStarted;
        let tryContinue = isInProgress;

        description = getTransText(<Trans>Begin your billing account validation process.</Trans>, i18n);
        if (isInProgress) {
            if (billing_account.kycInfo?.data?.start?.customer_identifier !== billing_account.account.email) {
                tryContinue = false;
                description = getTransText(
                    <Trans>Your email address has changed since you started validation. Please start over.</Trans>,
                    i18n,
                );
                mentionEmailLink = false;
            } else {
                description = getTransText(<Trans>You have billing account validation in progress.</Trans>, i18n);
                if (showRefresh) {
                    button = [
                        <StartDigioValidationButton
                            key="continue"
                            digio={digio}
                            isLoading={digio === undefined}
                            billing_account_id={billing_account.id}
                            try_continue={true}
                            callback={() => setShowRefresh(true)}
                        />,
                        <RefreshStatusButton key="refresh" />,
                    ];
                    mentionEmailLink = false;
                } else {
                    description += ` ${getTransText(<Trans>Please continue.</Trans>, i18n)}`;
                }
            }
        }

        if (config.provider_conf.notify_customer && mentionEmailLink) {
            // Mention the email link as a separate translation so we can mix and match text according to the config.
            description +=
                " " +
                getTransText(
                    <Trans>
                        You can do this from here and continue it later by clicking the link in the email we send you.
                    </Trans>,
                    i18n,
                );
        }

        if (loadError === undefined && button === undefined) {
            button = [
                <StartDigioValidationButton
                    key="start"
                    digio={digio}
                    isLoading={digio === undefined}
                    billing_account_id={billing_account.id}
                    try_continue={tryContinue}
                    callback={() => setShowRefresh(true)}
                />,
            ];
        }
    }

    return (
        <GeneralBaValidationBlock {...generalProps} {...{ title, description, button, color }}>
            {loadError && <ErrorMessage error={loadError} />}
        </GeneralBaValidationBlock>
    );
}
